import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  AccountingIntegrationsStatus,
  XeroConnectionStatus,
  XeroSyncResult,
} from '@/lib/types';

const INTEGRATIONS_API = '/api/v1/integrations';

// ---------------------------------------------------------------------------
// Cross-integration status (used by Settings > Integrations page)
// ---------------------------------------------------------------------------

export function useAccountingIntegrationsStatus() {
  return useQuery({
    queryKey: queryKeys.accountingIntegrations.status(),
    queryFn: async (): Promise<AccountingIntegrationsStatus> => {
      const response = await apiClient.get<AccountingIntegrationsStatus>(
        `${INTEGRATIONS_API}/accounting/status/`,
      );
      return response.data;
    },
  });
}

export function useSetDefaultAccountingIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      integration: 'quickbooks' | 'xero',
    ): Promise<{ default_integration: string }> => {
      const response = await apiClient.post<{ default_integration: string }>(
        `${INTEGRATIONS_API}/accounting/default/`,
        { integration },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accountingIntegrations.all });
      qc.invalidateQueries({ queryKey: queryKeys.xero.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Xero
// ---------------------------------------------------------------------------

export function useXeroStatus() {
  return useQuery({
    queryKey: queryKeys.xero.status(),
    queryFn: async (): Promise<XeroConnectionStatus> => {
      const response = await apiClient.get<XeroConnectionStatus>(
        `${INTEGRATIONS_API}/xero/status/`,
      );
      return response.data;
    },
  });
}

export function useXeroConnect() {
  return useMutation({
    mutationFn: async (): Promise<{ authorization_url: string; state: string }> => {
      const response = await apiClient.get<{ authorization_url: string; state: string }>(
        `${INTEGRATIONS_API}/xero/connect/`,
      );
      return response.data;
    },
  });
}

export function useXeroDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.post(`${INTEGRATIONS_API}/xero/disconnect/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.xero.all });
      qc.invalidateQueries({ queryKey: queryKeys.accountingIntegrations.all });
    },
  });
}

export function useXeroSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<XeroSyncResult> => {
      const response = await apiClient.post<XeroSyncResult>(
        `${INTEGRATIONS_API}/xero/sync/`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.xero.all });
    },
  });
}
